'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import './vector-case-study.css'

/**
 * Vector case study.
 *
 * A direct port of `public/radar-branding/vector-product-page.html`. That file
 * is the design contract: markup, class names, copy, and section order match it
 * one-for-one so the two can be diffed. Styling lives in the sibling
 * `vector-case-study.css`, scoped under `.vector-cs`.
 *
 * This renders full-bleed — `WorkPage`'s hero and container are skipped for
 * this slug (see `fullBleedProjects` in ./index.ts) because the case study
 * ships its own hero.
 *
 * Two deliberate breaks from the source:
 *   1. Its sticky nav, skip link, and its own page footer are dropped. The
 *      site header and footer already wrap this page and carry that
 *      navigation; the section ids they linked to stay.
 *   2. Its `<main id="main">` is dropped because the root layout already
 *      renders `<main id="main-content">`.
 *
 * The inline script it carried is reproduced below: the compass hover/focus
 * readout, the qualified/filtered profile switch, and the reveal observer.
 */

/** The twelve scoring dimensions, in the order the compass places them. */
const DIMENSIONS = [
  {
    n: '01',
    name: 'Intent',
    detail: 'Clear buying direction',
    score: '8 / 10',
    color: '#0db858',
  },
  {
    n: '02',
    name: 'Budget',
    detail: 'Allocated project budget',
    score: '7 / 10',
    color: '#0db858',
  },
  {
    n: '03',
    name: 'Timeline',
    detail: 'Active project this quarter',
    score: '9 / 10',
    color: '#3f82f7',
  },
  {
    n: '04',
    name: 'Authority',
    detail: 'Decision-maker identified',
    score: '8 / 10',
    color: '#ffa20c',
  },
  {
    n: '05',
    name: 'Need',
    detail: 'Explicit operational pain',
    score: '9 / 10',
    color: '#f23e67',
  },
  {
    n: '06',
    name: 'Fit',
    detail: 'Strong ICP match',
    score: '7 / 10',
    color: '#0aa9c9',
  },
  {
    n: '07',
    name: 'Engage',
    detail: 'Momentum is increasing',
    score: '8 / 10',
    color: '#655ce9',
  },
  {
    n: '08',
    name: 'Emotion',
    detail: 'Positive, urgent sentiment',
    score: '8 / 10',
    color: '#f46a22',
  },
  {
    n: '09',
    name: 'Context',
    detail: 'Previous sessions remembered',
    score: '9 / 10',
    color: '#16b7a7',
  },
  {
    n: '10',
    name: 'Knowledge',
    detail: 'Relevant answers retrieved',
    score: '8 / 10',
    color: '#d640df',
  },
  {
    n: '11',
    name: 'Integration',
    detail: 'CRM fields mapped',
    score: '6 / 10',
    color: '#655ce9',
  },
  {
    n: '12',
    name: 'Orchestrate',
    detail: 'Best next action selected',
    score: '7 / 10',
    color: '#0db858',
  },
] as const

const DEFAULT_ROUTE = 'Route to Senior AE ↗'
const DEFAULT_DETAIL = '12 dimensions agree: high purchase intent.'
const DEFAULT_COLOR = '#0db858'

/** The two sample leads behind the demo's tabs. */
const PROFILES = {
  qualified: {
    tab: 'Qualified lead',
    status: 'Qualified · high signal',
    initials: 'SC',
    name: 'Sarah Chen',
    role: 'VP of Operations · TechScale Inc.',
    score: '94',
    route: 'Route to Senior AE ↗',
    outcome: 'Meeting booked · closed in 23 days',
    note: 'Clear buying signals · active project · VP-level decision maker',
    color: '#0db858',
    values: [8, 7, 9, 8, 9, 7],
  },
  filtered: {
    tab: 'Filtered lead',
    status: 'Filtered · nurture',
    initials: 'JM',
    name: 'Jordan Miles',
    role: 'Research assistant · Northstar Labs',
    score: '41',
    route: 'Add to nurture sequence →',
    outcome: 'Re-engage when timeline becomes active',
    note: 'Research intent · no active budget · decision authority not established',
    color: '#f46a22',
    values: [4, 2, 3, 2, 5, 5],
  },
} as const

type ProfileKey = keyof typeof PROFILES

/** The six dimensions the score panel charts, with their track colours. */
const SCORE_ROWS = [
  { name: 'Intent', color: '#0db858' },
  { name: 'Budget', color: '#3f82f7' },
  { name: 'Timeline', color: '#655ce9' },
  { name: 'Authority', color: '#ffa20c' },
  { name: 'Need', color: '#f23e67' },
  { name: 'Fit', color: '#0aa9c9' },
] as const

const DIMENSION_CARDS = [
  {
    n: '01 · Direction',
    name: 'Intent',
    copy: 'Are they actively moving or only browsing?',
    color: '#0db858',
  },
  {
    n: '02 · Financial',
    name: 'Budget',
    copy: 'Can the move be funded and approved?',
    color: '#0db858',
  },
  {
    n: '03 · Urgency',
    name: 'Timeline',
    copy: 'Why now, and what date creates pressure?',
    color: '#3f82f7',
  },
  {
    n: '04 · Decision',
    name: 'Authority',
    copy: 'Who can commit, influence, or block?',
    color: '#ffa20c',
  },
  {
    n: '05 · Problem',
    name: 'Need',
    copy: 'Is the pain explicit, material, and understood?',
    color: '#f23e67',
  },
  {
    n: '06 · Solution',
    name: 'Fit',
    copy: 'Does the buyer match the real ICP?',
    color: '#0aa9c9',
  },
  {
    n: '07 · Momentum',
    name: 'Engagement',
    copy: 'Is attention deepening across the journey?',
    color: '#655ce9',
  },
  {
    n: '08 · Sentiment',
    name: 'Emotion',
    copy: 'What does tone reveal about readiness?',
    color: '#f46a22',
  },
  {
    n: '09 · Memory',
    name: 'Context',
    copy: 'What happened before this conversation?',
    color: '#16b7a7',
  },
  {
    n: '10 · Retrieval',
    name: 'Knowledge',
    copy: 'What must the agent know to be useful?',
    color: '#d640df',
  },
  {
    n: '11 · Connect',
    name: 'Integration',
    copy: 'Where should the signal be written?',
    color: '#655ce9',
  },
  {
    n: '12 · Action',
    name: 'Orchestration',
    copy: 'Which person or system acts next?',
    color: '#0db858',
  },
] as const

const TIMELINE = [
  {
    n: '01',
    title: 'Baseline',
    copy: 'Measure lead volume, qualification rate, response time, routing rules, and conversion by source. The system starts from real operating numbers.',
    color: '#3f82f7',
  },
  {
    n: '02',
    title: 'Qualify',
    copy: 'Score inbound conversations across all 12 dimensions. Each score carries evidence and contributes to an explainable decision.',
    color: '#655ce9',
  },
  {
    n: '03',
    title: 'Route',
    copy: 'Send high-signal leads to the right owner with context. Keep low-signal leads out of the active pipeline without deleting the trail.',
    color: '#f23e67',
  },
  {
    n: '04',
    title: 'Measure',
    copy: 'Compare Vector’s decisions with actual outcomes every week. Override mistakes, adjust weights, and improve the model from evidence.',
    color: '#0db858',
  },
] as const

export default function VectorProjectContent() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [enhanced, setEnhanced] = useState(false)
  const [profileKey, setProfileKey] = useState<ProfileKey>('qualified')
  const [readout, setReadout] = useState<{
    route: string
    detail: string
    color: string
  }>({ route: DEFAULT_ROUTE, detail: DEFAULT_DETAIL, color: DEFAULT_COLOR })

  // The source hides `[data-reveal]` unconditionally in CSS. Gating on the
  // mounted flag means a failed or blocked script leaves the page readable
  // instead of blank.
  useEffect(() => setEnhanced(true), [])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const targets = root.querySelectorAll<HTMLElement>('[data-reveal]')
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('is-visible'))
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -30px' }
    )

    targets.forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const showDimension = (d: (typeof DIMENSIONS)[number]) =>
    setReadout({
      route: `${d.name} · ${d.score}`,
      detail: d.detail,
      color: d.color,
    })

  const resetDimension = () =>
    setReadout({
      route: DEFAULT_ROUTE,
      detail: DEFAULT_DETAIL,
      color: DEFAULT_COLOR,
    })

  const profile = PROFILES[profileKey]

  return (
    <div ref={rootRef} className={`vector-cs${enhanced ? ' is-enhanced' : ''}`}>
      <section className='hero' aria-labelledby='hero-title'>
        <div className='hero-inner'>
          <div className='hero-copy'>
            <p className='eyebrow'>Pixelmojo work · AI sales system · 2025</p>
            <h1 id='hero-title'>
              Every lead, pointed to the{' '}
              <span className='headline-route'>right move.</span>
            </h1>
            <p className='hero-lede'>
              <strong>
                Vector is a qualification system built for B2B teams.
              </strong>{' '}
              It reads every conversation across 12 dimensions, explains the
              signal, and routes the next move—in real time.
            </p>
            <div className='hero-actions'>
              <Link
                className='button button-primary'
                href='/vector'
                data-cta-id='vector_case_explore'
              >
                Explore Vector{' '}
                <span className='arrow' aria-hidden='true'>
                  ↗
                </span>
              </Link>
              <a className='button button-ghost' href='#system'>
                See how it works{' '}
                <span className='arrow' aria-hidden='true'>
                  ↓
                </span>
              </a>
            </div>
            <dl className='vector-hero-meta'>
              <div>
                <dt>Role</dt>
                <dd>Strategy · Product · Build</dd>
              </div>
              <div>
                <dt>Delivery</dt>
                <dd>60-day fixed scope</dd>
              </div>
              <div>
                <dt>Model</dt>
                <dd>Client-owned application</dd>
              </div>
            </dl>
          </div>

          <div
            className='compass-wrap'
            role='group'
            aria-label='Twelve-dimension qualification compass'
          >
            <div className='compass-stage'>
              <span className='orbit-label top'>
                12 signals / one routing decision
              </span>
              <span className='orbit-label right'>
                Live qualification profile
              </span>
              <div
                className='decision-card'
                style={
                  { '--route-color': readout.color } as React.CSSProperties
                }
              >
                <div className='decision-top'>
                  <span>Lead profile</span>
                  <span className='decision-live'>live</span>
                </div>
                <div>
                  <div className='decision-score'>
                    94 <small>/ 120</small>
                  </div>
                  <div className='decision-route'>{readout.route}</div>
                  <div className='decision-detail' aria-live='polite'>
                    {readout.detail}
                  </div>
                </div>
              </div>

              {DIMENSIONS.map((d, i) => (
                <button
                  key={d.n}
                  className={`dimension d${i + 1}`}
                  type='button'
                  onMouseEnter={() => showDimension(d)}
                  onMouseLeave={resetDimension}
                  onFocus={() => showDimension(d)}
                  onBlur={resetDimension}
                >
                  <span className='dimension-dot'>{d.n}</span>
                  <span className='dimension-name'>{d.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='impact-rail' aria-label='Case study outcomes'>
        <div className='impact-inner'>
          <div className='impact-cell impact-intro'>
            <span className='impact-label'>The system</span>
            <strong>Less noise. More signal.</strong>
          </div>
          <div className='impact-cell'>
            <span className='impact-label'>Qualification model</span>
            <strong>12D</strong>
            <span>Evidence from twelve dimensions</span>
          </div>
          <div className='impact-cell'>
            <span className='impact-label'>Fixed-scope build</span>
            <strong>60d</strong>
            <span>Configured to the client’s ICP</span>
          </div>
          <div className='impact-cell'>
            <span className='impact-label'>Coverage</span>
            <strong>24/7</strong>
            <span>No high-intent lead left waiting</span>
          </div>
        </div>
      </section>

      <section className='section' id='challenge'>
        <div className='wrap problem-grid'>
          <div className='problem-heading' data-reveal>
            <p className='section-kicker'>The signal problem</p>
            <h2 className='section-title'>
              A full pipeline can still be empty.
            </h2>
          </div>
          <div className='problem-copy' data-reveal>
            <p className='lead-quote'>
              Sales teams were spending most of their attention on leads that
              would never convert—while real buyers waited in the same queue.
            </p>
            <p className='section-lede'>
              Traditional forms captured a name and email. Basic chatbots
              captured a transcript. Neither could tell the team who was ready,
              why they were ready, or what should happen next. Vector turns the
              conversation itself into an explainable qualification profile.
            </p>
            <div
              className='before-after'
              role='list'
              aria-label='Before and after Vector'
            >
              {[
                ['Name + email', '12-dimensional profile'],
                ['Static lead score', 'Live intent signal'],
                ['Manual triage', 'Explainable routing'],
                ['Lost context', 'Cross-session memory'],
              ].map(([before, after]) => (
                <div className='shift-row' role='listitem' key={before}>
                  <span>{before}</span>
                  <span className='shift-arrow' aria-hidden='true'>
                    →
                  </span>
                  <span>{after}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className='section product-section'
        id='system'
        aria-labelledby='system-title'
      >
        <div className='wrap'>
          <div className='product-head' data-reveal>
            <div>
              <p className='section-kicker'>The product moment</p>
              <h2 className='section-title' id='system-title'>
                You see the math, not a black box.
              </h2>
            </div>
            <p className='section-lede'>
              Every score affects the route. Every route keeps its rationale.
              Your team can review, override, and improve the system as real
              outcomes come in.
            </p>
          </div>

          <div className='demo-shell' data-reveal>
            <div className='demo-toolbar'>
              <div className='demo-brand'>Vector / Lead intelligence</div>
              <div
                className='demo-tabs'
                role='tablist'
                aria-label='Sample lead states'
              >
                {(Object.keys(PROFILES) as ProfileKey[]).map(key => (
                  <button
                    key={key}
                    className='demo-tab'
                    type='button'
                    role='tab'
                    aria-selected={profileKey === key}
                    aria-controls='leadPanel'
                    onClick={() => setProfileKey(key)}
                  >
                    {PROFILES[key].tab}
                  </button>
                ))}
              </div>
            </div>
            <div
              className='demo-grid'
              id='leadPanel'
              role='tabpanel'
              aria-live='polite'
            >
              <div className='lead-summary'>
                <div
                  className='lead-status'
                  style={{
                    color: profile.color,
                    background: `${profile.color}22`,
                  }}
                >
                  {profile.status}
                </div>
                <div
                  className='lead-avatar'
                  style={{
                    background: `linear-gradient(135deg, ${profile.color}, #655ce9)`,
                  }}
                >
                  {profile.initials}
                </div>
                <h3>{profile.name}</h3>
                <p className='lead-role'>{profile.role}</p>
                <div className='lead-score-row'>
                  <div>
                    <small>Total score</small>
                    <div className='big-score'>
                      <span>{profile.score}</span> <span>/ 120</span>
                    </div>
                  </div>
                  <div className='route-block'>
                    <small>Routing</small>
                    <strong>{profile.route}</strong>
                  </div>
                </div>
                <div className='outcome'>
                  <small>Observed outcome</small>
                  <span>{profile.outcome}</span>
                </div>
              </div>
              <div className='score-panel'>
                <div className='score-panel-header'>
                  <h3>Qualification profile</h3>
                  <span>Top 6 of 12 dimensions</span>
                </div>
                <div className='score-list'>
                  {SCORE_ROWS.map((row, i) => {
                    const value = profile.values[i]
                    return (
                      <div className='score-item' key={row.name}>
                        <span className='score-name'>{row.name}</span>
                        <span className='score-track'>
                          {/* `--w` alone drives the bar: the sheet declares
                              `width: var(--w)` with a width transition, so
                              switching profiles animates the track. */}
                          <span
                            className='score-fill'
                            style={
                              {
                                '--w': `${value * 10}%`,
                                '--c': row.color,
                              } as React.CSSProperties
                            }
                          />
                        </span>
                        <span className='score-value'>{value}/10</span>
                      </div>
                    )
                  })}
                </div>
                <p className='score-note'>{profile.note}</p>
              </div>
            </div>
          </div>
          <p className='demo-caption'>
            Illustrative product view · actual scoring weights and routing rules
            are configured to each client’s ICP.
          </p>
        </div>
      </section>

      <section
        className='section dimensions-section'
        aria-labelledby='dimensions-title'
      >
        <div className='wrap'>
          <div className='dimensions-head' data-reveal>
            <div>
              <p className='section-kicker'>The intelligence model</p>
              <h2 className='section-title' id='dimensions-title'>
                One lead. Twelve angles on the decision.
              </h2>
            </div>
            <p className='section-lede'>
              Vector scores what a buyer says, how the conversation changes, and
              what the business already knows—then combines the evidence into
              one next move.
            </p>
          </div>
          <div className='dimensions-grid' data-reveal>
            {DIMENSION_CARDS.map(card => (
              <article
                className='dimension-card'
                key={card.n}
                style={{ '--c': card.color } as React.CSSProperties}
              >
                <div className='card-number'>{card.n}</div>
                <h3>{card.name}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className='section results-section'
        id='results'
        aria-labelledby='results-title'
      >
        <div className='wrap'>
          <div className='results-head' data-reveal>
            <p className='section-kicker'>Measure, don’t promise</p>
            <h2 className='section-title' id='results-title'>
              The result is a loop you can prove.
            </h2>
          </div>
          <div className='results-grid'>
            <article className='result-card' data-reveal>
              <span className='result-index'>01 · Starting point</span>
              <div className='result-value'>W1</div>
              <h3>Baseline before automation</h3>
              <p>
                Week one measures lead volume, qualification rate, response
                time, conversion by source, and time spent per lead.
              </p>
            </article>
            <article className='result-card' data-reveal>
              <span className='result-index'>02 · Operating target</span>
              <div className='result-value'>20–40%</div>
              <h3>SDR time recovered</h3>
              <p>
                A 90-day target—not a guarantee—measured against the client’s
                actual baseline and reviewed with the team.
              </p>
            </article>
            <article className='result-card' data-reveal>
              <span className='result-index'>03 · Human authority</span>
              <div className='result-value'>1×</div>
              <h3>One-click override</h3>
              <p>
                Every route keeps its rationale. The team can correct a decision
                immediately and compare calls with outcomes weekly.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section
        className='section process-section'
        id='build'
        aria-labelledby='build-title'
      >
        <div className='wrap process-layout'>
          <div className='process-intro' data-reveal>
            <p className='section-kicker'>How it was built</p>
            <h2 className='section-title' id='build-title'>
              Baseline first. Then build the loop.
            </h2>
            <p className='section-lede'>
              Vector is not a generic scoring layer dropped onto a funnel. The
              system is configured around the client’s ICP, evidence, CRM, and
              real routing decisions.
            </p>
            <div
              className='stack-list'
              role='list'
              aria-label='Technology stack'
            >
              {[
                'Next.js',
                'TypeScript',
                'PostgreSQL',
                'Supabase',
                'OpenAI',
                'Vercel',
              ].map(tech => (
                <span role='listitem' key={tech}>
                  {tech}
                </span>
              ))}
            </div>
          </div>
          <div className='timeline' data-reveal>
            {TIMELINE.map(step => (
              <article
                className='timeline-step'
                key={step.n}
                style={{ '--c': step.color } as React.CSSProperties}
              >
                <span className='timeline-number'>{step.n}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className='ownership' aria-labelledby='ownership-title'>
        <div className='wrap ownership-card' data-reveal>
          <div>
            <p
              className='section-kicker'
              style={{ color: 'rgba(255,255,255,.62)' }}
            >
              The ownership model
            </p>
            <h2 className='section-title' id='ownership-title'>
              Built as an asset. Not rented as software.
            </h2>
          </div>
          <div className='ownership-copy'>
            <p>
              Pixelmojo builds Vector on infrastructure the client controls. The
              application code stays with the business; the intelligence
              platform keeps improving behind it.
            </p>
            <div className='ownership-points'>
              {[
                'Full application source access',
                'Standard, maintainable stack',
                'Visible data and routing logic',
                'No multi-tenant data mixing',
              ].map((point, i) => (
                <div className='ownership-point' key={point}>
                  <span>{`0${i + 1}`}</span>
                  {point}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='closing' aria-labelledby='closing-title'>
        <div className='wrap'>
          <div className='closing-grid' data-reveal>
            <h2 id='closing-title'>
              Stop counting leads. <span>Start reading them.</span>
            </h2>
            <div className='closing-copy'>
              <p>
                See the full Vector offer, fit criteria, pricing model, and
                delivery approach—or talk with Pixelmojo about your own
                qualification system.
              </p>
              <div className='closing-actions'>
                <Link
                  className='button button-primary'
                  href='/vector'
                  data-cta-id='vector_case_explore_closing'
                >
                  Explore Vector{' '}
                  <span className='arrow' aria-hidden='true'>
                    ↗
                  </span>
                </Link>
                <Link
                  className='button button-ghost'
                  href='/contact-us'
                  data-cta-id='vector_case_start'
                >
                  Start a project{' '}
                  <span className='arrow' aria-hidden='true'>
                    ↗
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
